import { class_of_use_enum, country_enum, energy_source_type_enum } from '@predium/enums';

/**
 * Returns the primary energy coefficient for a given energy source and country.
 *
 * For the UK, coefficients differ between residential (SAP 10.3, Jan 2026) and commercial
 * (NCM 2021, in force Jun 2022) buildings. Pass `classOfUse` when the building type is
 * known; omits default to COMMERCIAL.
 */
export function getPrimaryEnergyCoefficientForEnergySource(
  country: country_enum,
  energySource: energy_source_type_enum,
  classOfUse?: class_of_use_enum,
): number {
  if (country === country_enum.UK) {
    const useClassOfUse = classOfUse ?? class_of_use_enum.COMMERCIAL;
    return UKPrimaryEnergyCoefficientForEnergySource[useClassOfUse][energySource];
  }
  return PrimaryEnergyCoefficientForEnergySource[country][energySource];
}

/**
 * UK primary energy factors split by class of use.
 * Residential: SAP 10.3 (last updated January 2026; next update expected late 2026/early 2027).
 * Commercial: NCM 2021 (published December 2021, in force 15 June 2022).
 * Scotland's separate NCM currently matches exactly but may diverge in future cycles.
 */
const UKPrimaryEnergyCoefficientForEnergySource: Record<class_of_use_enum, Record<energy_source_type_enum, number>> = {
  [class_of_use_enum.RESIDENTIAL]: {
    [energy_source_type_enum.NATURAL_GAS]: 1.12,
    [energy_source_type_enum.FUEL_OIL]: 1.136,
    [energy_source_type_enum.COAL]: 1.096,
    [energy_source_type_enum.LIGNITE]: 1.096,
    [energy_source_type_enum.LPG]: 1.104,
    [energy_source_type_enum.BIO_GAS]: 1.442,
    [energy_source_type_enum.WOODEN_PELLETS]: 1.306,
    [energy_source_type_enum.WOOD]: 1.069,
    [energy_source_type_enum.ELECTRICITY_MIX]: 1.969,
    [energy_source_type_enum.ELECTRICITY_GREEN]: 0.969,
    [energy_source_type_enum.SOLAR]: 0,
    [energy_source_type_enum.DISTRICT_HEATING_CHP_FOSSIL_COAL]: 1.096,
    [energy_source_type_enum.DISTRICT_HEATING_CHP_FOSSIL_GAS]: 1.12,
    [energy_source_type_enum.DISTRICT_HEATING_CHP_RENEWABLE]: 1.037,
    [energy_source_type_enum.DISTRICT_HEATING_PLANTS_FOSSIL_COAL]: 1.096,
    [energy_source_type_enum.DISTRICT_HEATING_PLANTS_FOSSIL_GAS]: 1.12,
    [energy_source_type_enum.DISTRICT_HEATING_PLANTS_RENEWABLE]: 1.037,
    // SAP 10.3 does not define a factor for unspecified sources; using NATURAL_GAS as fallback
    [energy_source_type_enum.UNSPECIFIED]: 1.12,
  },
  [class_of_use_enum.COMMERCIAL]: {
    [energy_source_type_enum.NATURAL_GAS]: 1.126,
    [energy_source_type_enum.FUEL_OIL]: 1.18,
    [energy_source_type_enum.COAL]: 1.064,
    [energy_source_type_enum.LIGNITE]: 1.064,
    [energy_source_type_enum.LPG]: 1.141,
    [energy_source_type_enum.BIO_GAS]: 1.286,
    [energy_source_type_enum.WOODEN_PELLETS]: 1.037,
    [energy_source_type_enum.WOOD]: 1.037,
    [energy_source_type_enum.ELECTRICITY_MIX]: 1.55,
    [energy_source_type_enum.ELECTRICITY_GREEN]: 1.55,
    [energy_source_type_enum.SOLAR]: 0,
    [energy_source_type_enum.DISTRICT_HEATING_CHP_FOSSIL_COAL]: 1.064,
    [energy_source_type_enum.DISTRICT_HEATING_CHP_FOSSIL_GAS]: 1.13,
    [energy_source_type_enum.DISTRICT_HEATING_CHP_RENEWABLE]: 1.037,
    [energy_source_type_enum.DISTRICT_HEATING_PLANTS_FOSSIL_COAL]: 1.064,
    [energy_source_type_enum.DISTRICT_HEATING_PLANTS_FOSSIL_GAS]: 1.13,
    [energy_source_type_enum.DISTRICT_HEATING_PLANTS_RENEWABLE]: 1.037,
    // NCM 2021 does not define a factor for unspecified sources; using NATURAL_GAS as fallback
    [energy_source_type_enum.UNSPECIFIED]: 1.126,
  },
};

const PrimaryEnergyCoefficientForEnergySource: Record<
  Exclude<country_enum, country_enum.UK>,
  Record<energy_source_type_enum, number>
> = {
  [country_enum.DE]: {
    [energy_source_type_enum.NATURAL_GAS]: 1.1,
    [energy_source_type_enum.FUEL_OIL]: 1.1,
    [energy_source_type_enum.COAL]: 1.1,
    [energy_source_type_enum.LIGNITE]: 1.1,
    [energy_source_type_enum.LPG]: 1.1,
    [energy_source_type_enum.BIO_GAS]: 1.1,
    [energy_source_type_enum.WOODEN_PELLETS]: 0.2,
    [energy_source_type_enum.WOOD]: 0.2,
    [energy_source_type_enum.ELECTRICITY_MIX]: 1.8,
    [energy_source_type_enum.ELECTRICITY_GREEN]: 1.8,
    [energy_source_type_enum.SOLAR]: 0,
    [energy_source_type_enum.DISTRICT_HEATING_CHP_FOSSIL_COAL]: 0.7,
    [energy_source_type_enum.DISTRICT_HEATING_CHP_FOSSIL_GAS]: 0.7,
    [energy_source_type_enum.DISTRICT_HEATING_CHP_RENEWABLE]: 0,
    [energy_source_type_enum.DISTRICT_HEATING_PLANTS_FOSSIL_COAL]: 1.3,
    [energy_source_type_enum.DISTRICT_HEATING_PLANTS_FOSSIL_GAS]: 1.3,
    [energy_source_type_enum.DISTRICT_HEATING_PLANTS_RENEWABLE]: 0.1,
    [energy_source_type_enum.UNSPECIFIED]: 1.3,
  },
  [country_enum.PL]: {
    [energy_source_type_enum.NATURAL_GAS]: 1.1,
    [energy_source_type_enum.FUEL_OIL]: 1.1,
    [energy_source_type_enum.COAL]: 1.1,
    [energy_source_type_enum.LIGNITE]: 1.1,
    [energy_source_type_enum.LPG]: 1.1,
    [energy_source_type_enum.BIO_GAS]: 0.5,
    [energy_source_type_enum.WOODEN_PELLETS]: 0.2,
    [energy_source_type_enum.WOOD]: 0.2,
    [energy_source_type_enum.ELECTRICITY_MIX]: 2.5,
    [energy_source_type_enum.ELECTRICITY_GREEN]: 0,
    [energy_source_type_enum.SOLAR]: 0,
    [energy_source_type_enum.DISTRICT_HEATING_CHP_FOSSIL_COAL]: 0.8,
    [energy_source_type_enum.DISTRICT_HEATING_CHP_FOSSIL_GAS]: 0.8,
    [energy_source_type_enum.DISTRICT_HEATING_CHP_RENEWABLE]: 0.15,
    [energy_source_type_enum.DISTRICT_HEATING_PLANTS_FOSSIL_COAL]: 1.3,
    [energy_source_type_enum.DISTRICT_HEATING_PLANTS_FOSSIL_GAS]: 1.2,
    [energy_source_type_enum.DISTRICT_HEATING_PLANTS_RENEWABLE]: 0.15,
    [energy_source_type_enum.UNSPECIFIED]: 1.3,
  },
  [country_enum.AT]: {
    [energy_source_type_enum.NATURAL_GAS]: 1.1,
    [energy_source_type_enum.FUEL_OIL]: 1.2,
    [energy_source_type_enum.COAL]: 1.46,
    [energy_source_type_enum.LIGNITE]: 1.46,
    [energy_source_type_enum.LPG]: 1.2,
    [energy_source_type_enum.BIO_GAS]: 1.13,
    [energy_source_type_enum.WOODEN_PELLETS]: 1.13,
    [energy_source_type_enum.WOOD]: 1.13,
    [energy_source_type_enum.ELECTRICITY_MIX]: 1.43,
    [energy_source_type_enum.ELECTRICITY_GREEN]: 1.43,
    [energy_source_type_enum.SOLAR]: 0,
    [energy_source_type_enum.DISTRICT_HEATING_CHP_FOSSIL_COAL]: 0.71,
    [energy_source_type_enum.DISTRICT_HEATING_CHP_FOSSIL_GAS]: 0.71,
    [energy_source_type_enum.DISTRICT_HEATING_CHP_RENEWABLE]: 0.71,
    [energy_source_type_enum.DISTRICT_HEATING_PLANTS_FOSSIL_COAL]: 1.19,
    [energy_source_type_enum.DISTRICT_HEATING_PLANTS_FOSSIL_GAS]: 1.19,
    [energy_source_type_enum.DISTRICT_HEATING_PLANTS_RENEWABLE]: 1.27,
    [energy_source_type_enum.UNSPECIFIED]: 1.46,
  },
  [country_enum.FR]: {
    [energy_source_type_enum.NATURAL_GAS]: 1,
    [energy_source_type_enum.FUEL_OIL]: 1,
    [energy_source_type_enum.COAL]: 1,
    [energy_source_type_enum.LIGNITE]: 1,
    [energy_source_type_enum.LPG]: 1,
    [energy_source_type_enum.BIO_GAS]: 1,
    [energy_source_type_enum.WOODEN_PELLETS]: 1,
    [energy_source_type_enum.WOOD]: 1,
    [energy_source_type_enum.ELECTRICITY_MIX]: 1.9,
    [energy_source_type_enum.ELECTRICITY_GREEN]: 1.9,
    [energy_source_type_enum.SOLAR]: 0,
    [energy_source_type_enum.DISTRICT_HEATING_CHP_FOSSIL_COAL]: 1,
    [energy_source_type_enum.DISTRICT_HEATING_CHP_FOSSIL_GAS]: 1,
    [energy_source_type_enum.DISTRICT_HEATING_CHP_RENEWABLE]: 1,
    [energy_source_type_enum.DISTRICT_HEATING_PLANTS_FOSSIL_COAL]: 1,
    [energy_source_type_enum.DISTRICT_HEATING_PLANTS_FOSSIL_GAS]: 1,
    [energy_source_type_enum.DISTRICT_HEATING_PLANTS_RENEWABLE]: 1,
    [energy_source_type_enum.UNSPECIFIED]: 1,
  },
  [country_enum.CZ]: {
    [energy_source_type_enum.NATURAL_GAS]: 1.0,
    [energy_source_type_enum.FUEL_OIL]: 1.2,
    [energy_source_type_enum.COAL]: 1.0,
    [energy_source_type_enum.LIGNITE]: 1.0,
    [energy_source_type_enum.LPG]: 1.2,
    [energy_source_type_enum.BIO_GAS]: 1.0,
    [energy_source_type_enum.WOODEN_PELLETS]: 0.1,
    [energy_source_type_enum.WOOD]: 0.1,
    [energy_source_type_enum.ELECTRICITY_MIX]: 2.1,
    [energy_source_type_enum.ELECTRICITY_GREEN]: 0,
    [energy_source_type_enum.SOLAR]: 0,
    [energy_source_type_enum.DISTRICT_HEATING_CHP_FOSSIL_COAL]: 1.3,
    [energy_source_type_enum.DISTRICT_HEATING_CHP_FOSSIL_GAS]: 1.3,
    [energy_source_type_enum.DISTRICT_HEATING_CHP_RENEWABLE]: 0.7,
    [energy_source_type_enum.DISTRICT_HEATING_PLANTS_FOSSIL_COAL]: 1.3,
    [energy_source_type_enum.DISTRICT_HEATING_PLANTS_FOSSIL_GAS]: 1.3,
    [energy_source_type_enum.DISTRICT_HEATING_PLANTS_RENEWABLE]: 0.1,
    // TODO BLU-3060: placeholder (1.3) — Decree No. 222/2024 marks UNSPECIFIED as N/A; confirm CZ UNSPECIFIED primary-energy coefficient
    [energy_source_type_enum.UNSPECIFIED]: 1.3,
  },
};
